<template>
  <div
    v-click-outside="hide"
    class="multi-select"
    :class="{
      'multi-select--touched': somethingChecked,
      'multi-select--invalid': !isEmpty(errors),
    }"
  >
    <div class="multi-select-header" @click="open = !open">
      <div class="multi-select-title">
        <p class="title" :class="{ dark: open && !somethingChecked }">
          {{ title }}
        </p>
        <p v-if="somethingChecked" class="values">{{ displayKeys }}</p>
      </div>
      <div class="multi-select-icon">
        <dotcom-icon
          name="chevron-right"
          art="regular"
          class="caret-icon"
          rotation="90"
          :color="open ? '--c-primary-neutral-1' : '--c-secondary-neutral-2'"
          :class="{ isOpen: open || somethingChecked }"
        />
      </div>
    </div>
    <div
      v-show="open"
      class="multi-select-overlay"
      :class="{ scrollable: localOptions.length > 9 }"
    >
      <Checkbox
        v-model="selectAll"
        class="select-all"
        name="select-all"
        :text="$t('select-all')"
        @input="changeSelectAll"
      ></Checkbox>
      <div class="select-wrapper">
        <Checkbox
          v-for="option in localOptions"
          :key="option[displayValue]"
          v-model="option.checked"
          class="item"
          :name="option[displayKey]"
          :text="option[displayKey]"
          @input="emit"
        >
        </Checkbox>
      </div>
    </div>
  </div>
</template>

<script>
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import { isEmpty } from 'lodash'

import clickOutsideDirective from '@/directives/click-outside'

export default {
  name: 'MultiSelect',
  directives: {
    'click-outside': clickOutsideDirective,
  },

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    Checkbox: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/Checkbox'
      )
    },
  },

  props: {
    options: {
      type: Array,
      required: true,
    },

    displayKey: {
      type: String,
      default: 'name',
    },

    title: {
      type: String,
      default: 'Tätigkeitbereich',
    },

    displayValue: {
      type: String,
      default: 'value',
    },
    value: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },

  data: () => ({
    open: false,
    selectAll: false,
    errors: {},
    localOptions: [],
  }),

  computed: {
    displayKeys() {
      return this.$t('item-selected', {
        num: this.localOptions.filter(l => l.checked).length,
      })
    },
    somethingChecked() {
      return !!this.localOptions.find(l => l.checked)
    },
  },
  watch: {
    value: {
      handler(value) {
        this.localOptions = this.options.map(el => {
          return {
            ...el,
            checked: (value && value.includes(el[this.displayValue])) || false,
          }
        })
        this.selectAll = value && value.length === this.options.length
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    isEmpty,
    emit() {
      this.selectAll =
        this.localOptions && !this.localOptions.find(el => !el.checked)
      this.$emit(
        'input',
        this.localOptions
          .filter(el => el.checked)
          .map(el => el[this.displayValue]),
      )
    },
    hide() {
      if (this.open) {
        this.open = false
      }
    },
    changeSelectAll(value) {
      this.localOptions = this.localOptions.map(el => {
        return {
          ...el,
          checked: value,
        }
      })
      this.emit()
    },
  },
}
</script>

<style lang="postcss">
.multi-select {
  position: relative;
  display: inline-block;
  height: 65px;
  width: 100%;
  margin-bottom: 20px;
  color: var(--c-secondary-neutral-2) !important;
  padding-left: 10px;

  &--touched {
    .multi-select {
      &-header {
        .title {
          transition: all 0.25s ease;
          top: 0;
          position: absolute;
          font-size: 12px;
          left: 4px;
          margin: 0;
        }
      }
    }
  }

  &--invalid {
    .multi-select {
      border-bottom-color: var(--c-secondary-color-1);
      &-header {
        .title {
          color: var(--c-secondary-color-1);
        }
      }
    }
  }

  &-header {
    cursor: default;
    display: flex;
    height: 100%;
    justify-content: space-between;

    .title {
      color: var(--c-secondary-neutral-2);
      font-size: 16px;
      margin-top: auto;
      margin-bottom: auto;

      &.dark {
        color: var(--c-primary-neutral-1);
        font-size: 18px;
      }
    }
  }

  &-icon {
    display: flex;
    align-items: center;
    margin-right: var(--spacing-2);
    font-size: 16px;
  }

  &-title {
    width: calc(100% - 22px);
    align-items: center;
    display: flex;

    .values {
      margin-top: auto;
      margin-bottom: auto;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      color: var(--c-primary-neutral-1);
      font-size: 18px;
    }
  }

  &-overlay {
    min-width: 300px;
    z-index: 99;
    position: absolute;
    top: 50px;
    left: 0;
    right: 0;
    background-color: white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

    &.scrollable {
      height: 450px;
      overflow-y: auto;
    }

    .select-all {
      min-height: auto !important;
      border-bottom: 1px solid var(--c-secondary-neutral-3);
      display: flex;
      font-size: 20px;
      font-weight: bold;
      font-style: normal;
      line-height: 1.25;
      color: var(--c-primary-neutral-1);
      padding: 15px;
    }

    .select-wrapper {
      padding-bottom: 15px;
      border-bottom: 1px solid var(--c-secondary-neutral-3);
    }

    .item {
      padding: 15px 15px 0;
      min-height: auto !important;
      display: flex !important;
      font-size: 15px;
      font-weight: normal;
      font-style: normal;
      line-height: 1.33;
      color: var(--c-primary-neutral-1);

      &.form-element {
        margin-bottom: 0;
      }

      .checkbox__field {
        align-items: center;
      }

      .checkbox__label {
        margin-bottom: 3px;
      }
    }
  }
}
</style>
